import { FC, useMemo, useState } from 'react';
import { Search, User } from 'lucide-react';
import { Audience } from '../mock/types';
import { ChipPopover } from './ChipPopover';
import { fmtInt } from '../utils';

type AudienceChipProps = {
  audiences: Audience[];
  selectedId: string;
  onChange: (id: string) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const AudienceChip: FC<AudienceChipProps> = ({ audiences, selectedId, onChange, isOpen, onOpenChange }) => {
  const [query, setQuery] = useState('');
  const selected = audiences.find((a) => a.id === selectedId);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return audiences;
    return audiences.filter((a) => a.name.toLowerCase().includes(q));
  }, [audiences, query]);

  return (
    <ChipPopover
      icon={<User size={14} color='currentColor' />}
      label='Audience'
      value={selected?.name ?? '—'}
      popWidth={380}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      {(close) => (
        <>
          <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid #F0F2F5', position: 'relative' }}>
            <Search size={14} color='#9CA3B1' style={{ position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Cerca audience…'
              style={{
                width: '100%',
                border: '1px solid #DEE2E8',
                borderRadius: 8,
                padding: '8px 10px 8px 32px',
                fontSize: 13,
                outline: 'none',
                fontFamily: 'inherit',
                color: '#0B1733',
                background: '#F7F8FA',
              }}
            />
          </div>
          <div style={popSecStyle}>Audience attive nel workspace</div>
          <div style={{ maxHeight: 380, overflowY: 'auto', padding: '4px 0 6px' }}>
            {filtered.map((a) => {
              const isSelected = a.id === selectedId;
              return (
                <div
                  key={a.id}
                  onClick={() => {
                    onChange(a.id);
                    close();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    cursor: 'pointer',
                    fontSize: 13,
                    background: isSelected ? '#E8EFFF' : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) (e.currentTarget as HTMLElement).style.background = '#F7F8FA';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      display: 'inline-block',
                      marginLeft: 4,
                      flex: '0 0 7px',
                      background: a.status === 'live' ? '#16955E' : '#B97206',
                      boxShadow: a.status === 'live' ? '0 0 0 3px #DDF3E7' : '0 0 0 3px #FBEACE',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: isSelected ? '#0F3FBD' : '#0B1733', fontWeight: isSelected ? 600 : 500 }}>{a.name}</div>
                    <div style={{ fontSize: 11.5, color: '#7A8597' }}>
                      {fmtInt(a.usersCount)} utenti · attivata su {a.activeDestinationsCount} destinazion
                      {a.activeDestinationsCount === 1 ? 'e' : 'i'}
                    </div>
                  </div>
                  {isSelected ? <span style={{ color: '#0B1733', fontWeight: 700 }}>✓</span> : null}
                </div>
              );
            })}
          </div>
        </>
      )}
    </ChipPopover>
  );
};

const popSecStyle: React.CSSProperties = {
  padding: '8px 14px 4px',
  fontSize: 10.5,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#9CA3B1',
  fontWeight: 600,
};
